/**
 * Company Form Component (Multi-Step Wizard)
 * Reusable form for creating and editing companies
 * Aligned with backend Company table structure
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormHelperText,
  Typography,
  Alert,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Paper,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
} from '@mui/icons-material';
import {
  Company,
  CompanyFormData,
  CompanyType,
  ModuleType,
  CompanyStatus,
  ContactType,
  DocumentType,
  ESocialSendMode,
  MODULE_LABELS,
  COMPANY_STATUS_LABELS,
  COMPANY_TYPE_LABELS,
  CONTACT_TYPE_LABELS,
  DOCUMENT_TYPE_LABELS,
  ESOCIAL_SEND_MODE_LABELS,
  BRAZILIAN_STATES,
  formatCNPJ,
  formatCEP,
  formatPhone,
  removeCNPJMask,
  removeCEPMask,
  removePhoneMask,
  CompanyContact,
} from '../../../types';
import { companyService } from '../../../services/companyService';
import { fetchAddressByCEP } from '../../../utils/cepUtils';

// Form validation schema (aligned with backend Company table)
const companySchema = yup.object({
  legalName: yup
    .string()
    .required('Razão social é obrigatória')
    .min(2, 'Mínimo 2 caracteres'),
  tradeName: yup.string().notRequired(),
  cnpj: yup
    .string()
    .required('CNPJ é obrigatório')
    .test('cnpj-format', 'CNPJ deve conter 14 dígitos', (value) => {
      if (!value) return false;
      const numericOnly = value.replace(/\D/g, '');
      return numericOnly.length === 14;
    }),

  // Address fields (zipCode and state required in backend)
  zipCode: yup
    .string()
    .required('CEP é obrigatório')
    .test('cep-format', 'CEP deve conter 8 dígitos', (value) => {
      if (!value) return false;
      const numericOnly = value.replace(/\D/g, '');
      return numericOnly.length === 8;
    }),
  state: yup
    .string()
    .required('Estado é obrigatório')
    .oneOf(BRAZILIAN_STATES as unknown as string[], 'Estado inválido'),
  city: yup.string().notRequired(),
  neighborhood: yup.string().notRequired(),
  address: yup.string().notRequired(),
  addressNumber: yup.string().notRequired(),

  // Business info
  companyType: yup.string().notRequired(),
  status: yup.string().oneOf(Object.values(CompanyStatus)).default(CompanyStatus.ACTIVE),

  // Extended fields
  stateRegistration: yup.string().notRequired(),
  municipalRegistration: yup.string().notRequired(),
  email: yup.string().email('Email inválido').notRequired(),
  phone: yup.string().notRequired(),
  whatsapp: yup.string().notRequired(),
  ibgeCode: yup.string().notRequired(),
  documentType: yup.string().oneOf(Object.values(DocumentType)).notRequired(),
  documentNumber: yup.string().notRequired(),
  ecacExpirationDate: yup.string().notRequired(),
  esocialSendMode: yup.string().oneOf(Object.values(ESocialSendMode)).notRequired(),
  conditionStartDate: yup.string().notRequired(),
  parentCompanyId: yup.string().notRequired(),

  // Contacts by type (inline form)
  contacts: yup.object().notRequired(),

  // Modules (object with boolean values for each module)
  modules: yup.object({
    [ModuleType.MEDICINE]: yup.boolean().default(false),
    [ModuleType.SAFETY]: yup.boolean().default(false),
    [ModuleType.ENVIRONMENT]: yup.boolean().default(false),
    [ModuleType.ESOCIAL]: yup.boolean().default(false),
    [ModuleType.PSYCHOSOCIAL]: yup.boolean().default(false),
  }).required(),
}).required();

interface CompanyFormProps {
  company?: Company;
  onSubmit: (data: CompanyFormData) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  error?: string | null;
  mode?: 'create' | 'edit';
}

const STEPS = [
  'Dados da Empresa',
  'Endereço',
  'Contato e Registros',
  'Módulos e eSocial'
];

// Fields to validate for each step
const STEP_FIELDS: Record<number, string[]> = {
  0: ['legalName', 'cnpj'],
  1: ['zipCode', 'state'],
  2: [],
  3: []
};

const CompanyForm: React.FC<CompanyFormProps> = ({
  company,
  onSubmit,
  onCancel,
  loading = false,
  error,
  mode = 'create',
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [cnpjLookupLoading, setCnpjLookupLoading] = useState(false);
  const [cnpjLookupError, setCnpjLookupError] = useState<string | null>(null);
  const [cnpjLookupSuccess, setCnpjLookupSuccess] = useState<string | null>(null);
  const cnpjLookupAttemptedRef = useRef<string | null>(null);
  const [cnaeData, setCnaeData] = useState<Array<{
    code: string;
    description: string;
    riskLevel?: number;
    isPrimary: boolean;
  }>>([]);
  const [parentCompanyOptions, setParentCompanyOptions] = useState<Company[]>([]);
  const [parentCompanyLoading, setParentCompanyLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    trigger,
    watch,
  } = useForm<CompanyFormData>({
    resolver: yupResolver(companySchema) as any,
    defaultValues: {
      legalName: '',
      tradeName: '',
      cnpj: '',

      zipCode: '',
      state: '',
      city: '',
      neighborhood: '',
      address: '',
      addressNumber: '',

      companyType: '',
      status: CompanyStatus.ACTIVE,

      stateRegistration: '',
      municipalRegistration: '',
      email: '',
      phone: '',
      whatsapp: '',
      ibgeCode: '',
      documentType: DocumentType.CNPJ,
      documentNumber: '',
      ecacExpirationDate: '',
      esocialSendMode: ESocialSendMode.DONT_SEND,
      conditionStartDate: '',
      parentCompanyId: '',

      contacts: {
        [ContactType.COMMERCIAL]: { name: '', email: '', phone: '' },
        [ContactType.HR]: { name: '', email: '', phone: '' },
        [ContactType.ADMINISTRATIVE]: { name: '', email: '', phone: '' },
        [ContactType.ACCOUNTING]: { name: '', email: '', phone: '' },
        [ContactType.FINANCIAL]: { name: '', email: '', phone: '' },
      },

      modules: {
        [ModuleType.MEDICINE]: false,
        [ModuleType.SAFETY]: false,
        [ModuleType.ENVIRONMENT]: false,
        [ModuleType.ESOCIAL]: false,
        [ModuleType.PSYCHOSOCIAL]: false,
      },
    },
  });

  // Watch companyType to conditionally show parent company field
  const watchedCompanyType = watch('companyType');

  // Fetch potential parent companies when type is BRANCH
  const fetchParentCompanies = useCallback(async () => {
    setParentCompanyLoading(true);
    try {
      const response = await companyService.getCompanies({ limit: 100 });
      // Exclude the current company (in edit mode) from the list
      const options = (response.data || []).filter(
        (c) => !company?.id || String(c.id) !== String(company.id)
      );
      setParentCompanyOptions(options);
    } catch (err) {
      console.error('Failed to load parent companies:', err);
    } finally {
      setParentCompanyLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    if (watchedCompanyType === CompanyType.BRANCH && parentCompanyOptions.length === 0) {
      fetchParentCompanies();
    }
  }, [watchedCompanyType, parentCompanyOptions.length, fetchParentCompanies]);

  // Load company data when editing
  useEffect(() => {
    if (company && mode === 'edit') {
      // Convert modules array to object format for form
      const modulesObj = {
        [ModuleType.MEDICINE]: company.modules?.some(m => m.module === ModuleType.MEDICINE) || false,
        [ModuleType.SAFETY]: company.modules?.some(m => m.module === ModuleType.SAFETY) || false,
        [ModuleType.ENVIRONMENT]: company.modules?.some(m => m.module === ModuleType.ENVIRONMENT) || false,
        [ModuleType.ESOCIAL]: company.modules?.some(m => m.module === ModuleType.ESOCIAL) || false,
        [ModuleType.PSYCHOSOCIAL]: company.modules?.some(m => m.module === ModuleType.PSYCHOSOCIAL) || false,
      };

      // Build contacts object from existing contacts
      const contactsObj: Record<string, { name: string; email: string; phone: string }> = {
        [ContactType.COMMERCIAL]: { name: '', email: '', phone: '' },
        [ContactType.HR]: { name: '', email: '', phone: '' },
        [ContactType.ADMINISTRATIVE]: { name: '', email: '', phone: '' },
        [ContactType.ACCOUNTING]: { name: '', email: '', phone: '' },
        [ContactType.FINANCIAL]: { name: '', email: '', phone: '' },
      };

      reset({
        legalName: company.legalName ?? '',
        tradeName: company.tradeName ?? '',
        cnpj: formatCNPJ(company.cnpj ?? ''),

        zipCode: formatCEP(company.zipCode ?? ''),
        state: company.state ?? '',
        city: company.city ?? '',
        neighborhood: company.neighborhood ?? '',
        address: company.address ?? '',
        addressNumber: company.addressNumber ?? '',

        companyType: company.companyType ?? '',
        status: company.status || CompanyStatus.ACTIVE,

        stateRegistration: company.stateRegistration ?? '',
        municipalRegistration: company.municipalRegistration ?? '',
        email: company.email ?? '',
        phone: company.phone ? formatPhone(company.phone) : '',
        whatsapp: company.whatsapp ? formatPhone(company.whatsapp) : '',
        ibgeCode: company.ibgeCode ?? '',
        documentType: company.documentType || DocumentType.CNPJ,
        documentNumber: company.documentNumber ?? '',
        ecacExpirationDate: company.ecacExpirationDate?.split('T')[0] || '',
        esocialSendMode: company.esocialSendMode || ESocialSendMode.DONT_SEND,
        conditionStartDate: company.conditionStartDate?.split('T')[0] || '',
        parentCompanyId: company.parentCompanyId?.toString() || '',

        contacts: contactsObj,
        modules: modulesObj,
      });

      // Load contacts asynchronously after reset
      if (company.id) {
        companyService.getContacts(String(company.id)).then((existingContacts: CompanyContact[]) => {
          for (const contact of existingContacts) {
            const type = contact.contactType as ContactType;
            if (contactsObj[type]) {
              setValue(`contacts.${type}.name` as any, contact.name || '');
              setValue(`contacts.${type}.email` as any, contact.email || '');
              setValue(`contacts.${type}.phone` as any, contact.phone ? formatPhone(contact.phone) : '');
            }
          }
        }).catch(() => { /* Non-critical: contacts may not exist */ });
      }
    }
  }, [company, mode, reset, setValue]);

  // Handle CNPJ lookup
  const handleCNPJLookup = async () => {
    const cnpjValue = getValues('cnpj');
    const cleanCnpj = removeCNPJMask(cnpjValue);

    if (!cleanCnpj || cleanCnpj.length !== 14) {
      setCnpjLookupError('CNPJ deve ter 14 dígitos');
      return;
    }

    setCnpjLookupLoading(true);
    setCnpjLookupError(null);

    try {
      const companyData = await companyService.lookupCNPJ(cleanCnpj);

      // Fill form with the received data
      if (companyData.legalName) setValue('legalName', companyData.legalName);
      if (companyData.tradeName) setValue('tradeName', companyData.tradeName);
      if (companyData.zipCode) setValue('zipCode', formatCEP(companyData.zipCode));
      if (companyData.state) setValue('state', companyData.state);
      if (companyData.city) setValue('city', companyData.city);
      if (companyData.neighborhood) setValue('neighborhood', companyData.neighborhood);
      if (companyData.address) setValue('address', companyData.address);
      if (companyData.addressNumber) setValue('addressNumber', companyData.addressNumber);
      if (companyData.companyType) setValue('companyType', companyData.companyType);
      if (companyData.email) setValue('email', companyData.email);
      if (companyData.phone) {
        const firstPhone = companyData.phone.split('/')[0].trim();
        setValue('phone', formatPhone(firstPhone));
      }
      // Fetch IBGE code via ViaCEP using the CEP from CNPJ lookup
      if (companyData.zipCode) {
        fetchAddressByCEP(companyData.zipCode).then((addressData) => {
          if (addressData?.ibgeCode) setValue('ibgeCode', addressData.ibgeCode);
        }).catch(() => { /* IBGE lookup is non-critical */ });
      }

      // Collect CNAE data from lookup
      const cnaes: typeof cnaeData = [];
      if (companyData.cnaeMain) {
        cnaes.push({
          code: companyData.cnaeMain.code,
          description: companyData.cnaeMain.description,
          isPrimary: true,
        });
      }
      if (companyData.cnaeSecondary) {
        for (const sec of companyData.cnaeSecondary) {
          if (sec.code && sec.code !== '00.00-0-00') {
            cnaes.push({
              code: sec.code,
              description: sec.description,
              isPrimary: false,
            });
          }
        }
      }
      setCnaeData(cnaes);

      // Fetch risk levels from NR-4 reference table
      if (cnaes.length > 0) {
        const codes = cnaes.map(c => c.code.replace(/\D/g, ''));
        try {
          const riskData = await companyService.lookupCnaeRiskLevels(codes);
          setCnaeData(prev => prev.map(cnae => {
            const cleanCode = cnae.code.replace(/\D/g, '');
            const ref = riskData.find(r => r.code === cleanCode);
            return ref ? { ...cnae, riskLevel: ref.riskLevel } : cnae;
          }));
        } catch {
          // Risk level lookup is non-critical, keep CNAEs without risk level
        }
      }

      // Show success message
      setCnpjLookupSuccess(`Dados da empresa ${companyData.legalName} foram preenchidos automaticamente!`);

    } catch (err: any) {
      let errorMessage = 'Erro ao consultar CNPJ';

      if (err.response?.status === 401) {
        errorMessage = 'Usuario nao autenticado. Faca login novamente.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Sem permissão para consultar CNPJ.';
      } else if (err.response?.status === 404) {
        errorMessage = 'CNPJ não encontrado ou inválido.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setCnpjLookupError(errorMessage);
    } finally {
      setCnpjLookupLoading(false);
    }
  };

  // Auto-trigger CNPJ lookup when field is fully filled
  const watchedCnpj = watch('cnpj');
  useEffect(() => {
    const cleanCnpj = removeCNPJMask(watchedCnpj || '');
    if (cleanCnpj.length < 14) {
      cnpjLookupAttemptedRef.current = null;
      return;
    }
    if (cleanCnpj.length === 14 && !cnpjLookupLoading && cnaeData.length === 0 && mode === 'create' && cnpjLookupAttemptedRef.current !== cleanCnpj) {
      cnpjLookupAttemptedRef.current = cleanCnpj;
      handleCNPJLookup();
    }
    // handleCNPJLookup intentionally omitted — triggers only on CNPJ change, ref guard prevents loops
  }, [watchedCnpj, cnpjLookupLoading, cnaeData.length, mode]);

  const handleFormSubmit = async (data: CompanyFormData) => {
    try {
      // Convert modules object to array format for backend
      const moduleArray: ModuleType[] = [];
      if (data.modules[ModuleType.MEDICINE]) moduleArray.push(ModuleType.MEDICINE);
      if (data.modules[ModuleType.SAFETY]) moduleArray.push(ModuleType.SAFETY);
      if (data.modules[ModuleType.ENVIRONMENT]) moduleArray.push(ModuleType.ENVIRONMENT);
      if (data.modules[ModuleType.ESOCIAL]) moduleArray.push(ModuleType.ESOCIAL);
      if (data.modules[ModuleType.PSYCHOSOCIAL]) moduleArray.push(ModuleType.PSYCHOSOCIAL);

      // Extract filled contacts
      const filledContacts = Object.entries(data.contacts || {})
        .filter(([, v]) => v?.name?.trim())
        .map(([type, v]) => ({
          contactType: type,
          name: v!.name,
          email: v!.email || undefined,
          phone: v!.phone ? removePhoneMask(v!.phone) : undefined,
        }));

      // Remove masks from CNPJ, CEP, and phone before submitting
      const processedData = {
        ...data,
        cnpj: removeCNPJMask(data.cnpj),
        zipCode: removeCEPMask(data.zipCode),
        phone: data.phone ? removePhoneMask(data.phone) : undefined,
        whatsapp: data.whatsapp ? removePhoneMask(data.whatsapp) : undefined,
        modules: moduleArray, // Send as array to backend
      };

      // Os contatos saem SEMPRE como lista, inclusive vazia.
      //
      // Enquanto a lista vazia era omitida, `processedData` ficava com o `contacts` cru do
      // formulário — um objeto indexado por tipo de contato ({ RH: {...}, FINANCEIRO: {...} }) —
      // e a tela de edição, que espera lista, estourava "(contatos || []).map is not a function"
      // logo depois de a empresa já ter sido gravada. Quem editasse uma empresa sem contato
      // preenchido via erro vermelho num salvamento que tinha dado certo.
      //
      // A lista vazia também é informação: é ela que diz "apaguei o último contato".
      (processedData as any).contacts = filledContacts;

      // Attach CNAE data from CNPJ lookup if available
      if (cnaeData.length > 0) {
        (processedData as any).cnaes = cnaeData.map(c => ({
          cnaeCode: c.code.replace(/\D/g, ''),
          description: c.description,
          isPrimary: c.isPrimary,
        }));
      }

      await onSubmit(processedData as any);
    } catch (err) {
      console.error('Form submission error:', err);
    }
  };

  // Step navigation
  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Validate current step fields
    const fieldsToValidate = STEP_FIELDS[activeStep] ?? [];
    const isValid = fieldsToValidate.length === 0 || (await trigger(fieldsToValidate as any));

    if (isValid && activeStep < STEPS.length - 1) {
      const nextStep = activeStep + 1;

      // Auto-fill eSocial document number with CNPJ when entering step 3
      if (nextStep === 3 && !getValues('documentNumber')) {
        const cnpj = removeCNPJMask(getValues('cnpj'));
        if (cnpj) {
          setValue('documentNumber', cnpj);
        }
      }

      setActiveStep(nextStep);
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleStepClick = (step: number) => {
    if (step <= activeStep) {
      // Auto-fill eSocial document number with CNPJ when navigating to step 3
      if (step === 3 && !getValues('documentNumber')) {
        const cnpj = removeCNPJMask(getValues('cnpj'));
        if (cnpj) {
          setValue('documentNumber', cnpj);
        }
      }
      setActiveStep(step);
    }
  };

  const hasStepErrors = (step: number): boolean => {
    const fields = STEP_FIELDS[step] ?? [];
    return fields.some(field => !!(errors as any)[field]);
  };

  // Step 0: Company Data
  const renderCompanyData = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Dados da Empresa
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {/* Legal Name */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="legalName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Razão Social *"
                error={!!errors.legalName}
                helperText={errors.legalName?.message}
              />
            )}
          />
        </Grid>

        {/* Trade Name */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="tradeName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Nome Fantasia"
                error={!!errors.tradeName}
                helperText={errors.tradeName?.message}
              />
            )}
          />
        </Grid>

        {/* CNPJ */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="cnpj"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="CNPJ *"
                placeholder="XX.XXX.XXX/XXXX-XX"
                inputProps={{ maxLength: 18 }}
                error={!!errors.cnpj || !!cnpjLookupError}
                helperText={errors.cnpj?.message || cnpjLookupError}
                onChange={(e) => {
                  const formatted = formatCNPJ(e.target.value);
                  field.onChange(formatted);
                  if (cnpjLookupError) setCnpjLookupError(null);
                  if (cnpjLookupSuccess) setCnpjLookupSuccess(null);
                  if (cnaeData.length > 0) setCnaeData([]);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleCNPJLookup}
                        disabled={cnpjLookupLoading || !field.value}
                        size="small"
                        title="Buscar dados da empresa pelo CNPJ"
                      >
                        {cnpjLookupLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SearchIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Company Type */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="companyType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.companyType}>
                <InputLabel>Tipo de Empresa</InputLabel>
                <Select {...field} label="Tipo de Empresa">
                  <MenuItem value="">Selecione...</MenuItem>
                  {Object.entries(COMPANY_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.companyType && (
                  <FormHelperText>{errors.companyType.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Parent Company (only visible when type is BRANCH) */}
        {watchedCompanyType === CompanyType.BRANCH && (
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="parentCompanyId"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={parentCompanyOptions}
                  getOptionLabel={(option) =>
                    option.tradeName
                      ? `${option.tradeName} (${option.legalName})`
                      : option.legalName
                  }
                  value={parentCompanyOptions.find((c) => String(c.id) === field.value) || null}
                  onChange={(_, selected) => {
                    field.onChange(selected ? String(selected.id) : '');
                  }}
                  loading={parentCompanyLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Empresa Matriz"
                      placeholder="Selecione a empresa matriz..."
                      error={!!errors.parentCompanyId}
                      helperText={errors.parentCompanyId?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}

        {/* Status */}
        <Grid
          size={{
            xs: 12,
            md: watchedCompanyType === CompanyType.BRANCH ? 8 : 4
          }}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  {Object.entries(COMPANY_STATUS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.status && (
                  <FormHelperText>{errors.status.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
    </>
  );

  // Step 1: Address
  const renderAddress = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Endereço
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {/* CEP */}
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <Controller
            name="zipCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="CEP *"
                placeholder="XXXXX-XXX"
                inputProps={{ maxLength: 9 }}
                error={!!errors.zipCode}
                helperText={errors.zipCode?.message}
                onChange={(e) => {
                  const formatted = formatCEP(e.target.value);
                  field.onChange(formatted);
                }}
              />
            )}
          />
        </Grid>

        {/* State */}
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.state}>
                <InputLabel>Estado *</InputLabel>
                <Select {...field} label="Estado *">
                  <MenuItem value="">Selecione...</MenuItem>
                  {BRAZILIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
                {errors.state && (
                  <FormHelperText>{errors.state.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* City */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Cidade"
                error={!!errors.city}
                helperText={errors.city?.message}
              />
            )}
          />
        </Grid>

        {/* Neighborhood */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="neighborhood"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Bairro"
                error={!!errors.neighborhood}
                helperText={errors.neighborhood?.message}
              />
            )}
          />
        </Grid>

        {/* Address */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Logradouro"
                error={!!errors.address}
                helperText={errors.address?.message}
              />
            )}
          />
        </Grid>

        {/* Address Number */}
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <Controller
            name="addressNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Número"
                error={!!errors.addressNumber}
                helperText={errors.addressNumber?.message}
              />
            )}
          />
        </Grid>

        {/* IBGE Code */}
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <Controller
            name="ibgeCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Código IBGE"
                error={!!errors.ibgeCode}
                helperText={errors.ibgeCode?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );

  // Step 2: Contact and Registrations
  const renderContactAndRegistrations = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Contato da Empresa
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {/* Email */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="email"
                label="Email"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </Grid>

        {/* Phone */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Telefone"
                placeholder="(XX) XXXXX-XXXX"
                error={!!errors.phone}
                helperText={errors.phone?.message}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  field.onChange(formatted);
                }}
                inputProps={{ maxLength: 15 }}
              />
            )}
          />
        </Grid>

        {/* WhatsApp */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="whatsapp"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="WhatsApp"
                placeholder="(XX) XXXXX-XXXX"
                error={!!errors.whatsapp}
                helperText={errors.whatsapp?.message}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  field.onChange(formatted);
                }}
                inputProps={{ maxLength: 15 }}
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Inscrições e Registros
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {/* State Registration */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="stateRegistration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Inscrição Estadual"
                error={!!errors.stateRegistration}
                helperText={errors.stateRegistration?.message}
              />
            )}
          />
        </Grid>

        {/* Municipal Registration */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="municipalRegistration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Inscrição Municipal"
                error={!!errors.municipalRegistration}
                helperText={errors.municipalRegistration?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Contatos Responsáveis
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {[ContactType.COMMERCIAL, ContactType.HR, ContactType.ADMINISTRATIVE, ContactType.ACCOUNTING, ContactType.FINANCIAL].map((contactType) => (
        <Paper key={contactType} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {CONTACT_TYPE_LABELS[contactType]}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name={`contacts.${contactType}.name` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label="Nome"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name={`contacts.${contactType}.email` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    type="email"
                    label="Email"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name={`contacts.${contactType}.phone` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label="Telefone"
                    placeholder="(XX) XXXXX-XXXX"
                    size="small"
                    inputProps={{ maxLength: 15 }}
                    onChange={(e) => {
                      const formatted = formatPhone(e.target.value);
                      field.onChange(formatted);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      ))}
    </>
  );

  // Step 3: Modules and eSocial
  const renderModulesAndEsocial = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Módulos Contratados
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {/* Medicine */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name={`modules.${ModuleType.MEDICINE}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={MODULE_LABELS[ModuleType.MEDICINE]}
              />
            )}
          />
        </Grid>

        {/* Safety */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name={`modules.${ModuleType.SAFETY}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={MODULE_LABELS[ModuleType.SAFETY]}
              />
            )}
          />
        </Grid>

        {/* Environment */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name={`modules.${ModuleType.ENVIRONMENT}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={MODULE_LABELS[ModuleType.ENVIRONMENT]}
              />
            )}
          />
        </Grid>

        {/* eSocial */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name={`modules.${ModuleType.ESOCIAL}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={MODULE_LABELS[ModuleType.ESOCIAL]}
              />
            )}
          />
        </Grid>

        {/* Psicossocial — modulo proprio desde a reforma de acesso (#408, V7), e nao mais uma
            parte de Seguranca do Trabalho. Sem este interruptor a empresa nao tem como
            contrata-lo, e as telas de Psicossocial ficam invisiveis no contexto dela. */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name={`modules.${ModuleType.PSYCHOSOCIAL}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={MODULE_LABELS[ModuleType.PSYCHOSOCIAL]}
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Configurações eSocial
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {/* Document Type */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="documentType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.documentType}>
                <InputLabel>Tipo de Documento</InputLabel>
                <Select {...field} label="Tipo de Documento">
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.documentType && (
                  <FormHelperText>{errors.documentType.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Document Number */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="documentNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Número do Documento"
                error={!!errors.documentNumber}
                helperText={errors.documentNumber?.message}
              />
            )}
          />
        </Grid>

        {/* e-CAC Expiration Date */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Controller
            name="ecacExpirationDate"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="date"
                label="Validade e-CAC"
                InputLabelProps={{ shrink: true }}
                error={!!errors.ecacExpirationDate}
                helperText={errors.ecacExpirationDate?.message}
              />
            )}
          />
        </Grid>

        {/* eSocial Send Mode */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="esocialSendMode"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.esocialSendMode}>
                <InputLabel>Modo de Envio eSocial</InputLabel>
                <Select {...field} label="Modo de Envio eSocial">
                  {Object.entries(ESOCIAL_SEND_MODE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.esocialSendMode && (
                  <FormHelperText>{errors.esocialSendMode.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Condition Start Date (S-2240) */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="conditionStartDate"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="date"
                label="Início das Condições (S-2240)"
                InputLabelProps={{ shrink: true }}
                helperText="Data de início das condições ambientais de trabalho"
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );

  const isLastStep = activeStep === STEPS.length - 1;
  const isFirstStep = activeStep === 0;

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {cnpjLookupSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setCnpjLookupSuccess(null)}
        >
          {cnpjLookupSuccess}
        </Alert>
      )}

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label, index) => (
          <Step
            key={label}
            completed={index < activeStep}
            sx={{ cursor: index <= activeStep ? 'pointer' : 'default' }}
            onClick={() => handleStepClick(index)}
          >
            <StepLabel error={hasStepErrors(index)}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
        {renderCompanyData()}
      </Box>
      <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
        {renderAddress()}
      </Box>
      <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
        {renderContactAndRegistrations()}
      </Box>
      <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
        {renderModulesAndEsocial()}
      </Box>

      {/* CNAE Data from CNPJ Lookup */}
      {cnaeData.length > 0 && activeStep === 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            CNAEs identificados na Receita Federal
          </Typography>

          {/* Primary CNAE */}
          {cnaeData.filter(c => c.isPrimary).map(cnae => (
            <Box key={cnae.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Atividade Principal:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {cnae.code} - {cnae.description}
              </Typography>
              {cnae.riskLevel != null && (
                <Chip
                  label={`Grau de Risco: ${cnae.riskLevel}`}
                  size="small"
                  color={cnae.riskLevel >= 3 ? 'error' : cnae.riskLevel === 2 ? 'warning' : 'success'}
                  variant="outlined"
                />
              )}
            </Box>
          ))}

          {/* Secondary CNAEs */}
          {cnaeData.filter(c => !c.isPrimary).length > 0 && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 500, mt: 1, mb: 0.5 }}>
                Atividades Secundárias:
              </Typography>
              {cnaeData.filter(c => !c.isPrimary).map(cnae => (
                <Box key={cnae.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pl: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    {cnae.code} - {cnae.description}
                  </Typography>
                  {cnae.riskLevel != null && (
                    <Chip
                      label={`GR: ${cnae.riskLevel}`}
                      size="small"
                      color={cnae.riskLevel >= 3 ? 'error' : cnae.riskLevel === 2 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  )}
                </Box>
              ))}
            </>
          )}
        </Paper>
      )}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBack}
          disabled={isFirstStep}
        >
          Voltar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
          )}

          {!isLastStep ? (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
            >
              Próximo
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Salvando...' : mode === 'create' ? 'Criar Empresa' : 'Atualizar Empresa'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyForm;
